/**
 * Business switcher — dashboard header control to scope every page to one of
 * the businesses this agency manages.
 *
 * How it works
 * ------------
 * Each managed business is a distinct `org_id` slug in the reviews schema
 * (Animas Marketing + client businesses), mirrored 1:1 by
 * `reviews.business_locations`. Selecting a business writes its
 * `business_locations.id` to the admin store; the `X-Tenant-Id` request
 * interceptor (see `main.tsx`) then sends that id on every API call, and the
 * backend `ReviewsOrgSlugMiddleware` maps it to the `org_id` slug so all
 * queries scope to that business.
 *
 * With nothing explicitly selected (`selectedOrgId === null`), the backend
 * defaults to the primary business (Animas Marketing) via hostname resolution;
 * the control reflects that default without writing to the store.
 */

import { Select } from "@plerity/ui-core";
import { useQueryClient } from "@tanstack/react-query";
import { reviewsKeys } from "../api/keys";
import { useBusinesses } from "../api/queries";
import { useAdminStore } from "../lib/admin";

export function BusinessSwitcher() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useBusinesses();
  const selectedOrgId = useAdminStore((s) => s.selectedOrgId);
  const setSelectedOrgId = useAdminStore((s) => s.setSelectedOrgId);

  const businesses = data?.items ?? [];

  // Nothing to switch between — hide the control entirely.
  if (isLoading || businesses.length <= 1) {
    return null;
  }

  // The backend flags the default business (no hardcoded slug in the client).
  const defaultId = businesses.find((b) => b.is_default)?.id ?? businesses[0]?.id;
  const value = String(selectedOrgId ?? defaultId ?? "");

  const options = businesses.map((b) => ({
    value: String(b.id),
    label: `${b.location_name} (${b.review_count})`,
  }));

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextId = Number(event.target.value);
    if (Number.isNaN(nextId)) return;
    setSelectedOrgId(nextId);
    // Query keys don't vary by selected business (the org is carried on the
    // X-Tenant-Id header, read fresh per request), so a switch must invalidate
    // the reviews scope to force a refetch with the new tenant.
    void queryClient.invalidateQueries({ queryKey: reviewsKeys.all });
  };

  return (
    <div className="w-56">
      <Select
        aria-label="Select business to view reviews for"
        size="sm"
        options={options}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
}

export default BusinessSwitcher;
